import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { categoryLabel, categoryOrder } from '@/modules/automation/status';
import { catalogPlaybook, PLAYBOOK_DOCS, TEMPLATE_DOCS, type DocPage } from '@/modules/docs/content';
import type { PlaybookSummary } from '@/types/api';

/**
 * Every playbook page by category, or every template page, as cards.
 *
 * The same list on the public site and in the dashboard; `linkFor` decides where each card goes.
 */
export function ReferenceIndex({
  kind,
  linkFor,
}: {
  kind: 'playbook' | 'template';
  linkFor: (href: string) => string;
}) {
  if (kind === 'template') {
    return <Cards pages={TEMPLATE_DOCS} base="/docs/templates" linkFor={linkFor} />;
  }

  const groups = categoryOrder
    .map((category) => ({
      category,
      pages: PLAYBOOK_DOCS.filter((page) => catalogPlaybook(page.slug)?.category === category),
    }))
    .filter((group) => group.pages.length > 0);

  return (
    <div className="space-y-8">
      {groups.map((group) => (
        <section key={group.category} aria-labelledby={`docs-category-${group.category}`} className="space-y-3">
          <h3
            id={`docs-category-${group.category}`}
            className="text-muted-foreground text-[13px] font-semibold tracking-wider uppercase"
          >
            {categoryLabel[group.category as PlaybookSummary['category']]}
          </h3>
          <Cards pages={group.pages} base="/docs/playbooks" linkFor={linkFor} />
        </section>
      ))}
    </div>
  );
}

function Cards({ pages, base, linkFor }: { pages: DocPage[]; base: string; linkFor: (href: string) => string }) {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
      {pages.map((page) => (
        <Link
          key={page.slug}
          to={linkFor(`${base}/${page.slug}`)}
          className="border-border/60 hover:border-primary/40 hover:bg-muted/30 group flex items-start gap-3 rounded-xl border p-4 transition-colors"
        >
          <span className="min-w-0 flex-1">
            <span className="text-foreground flex items-center gap-2 font-medium">
              {page.title}
              <ArrowRight className="size-4 opacity-0 transition-opacity group-hover:opacity-70" aria-hidden />
            </span>
            <span className="text-muted-foreground mt-1 block text-sm leading-relaxed">{page.summary}</span>
          </span>
        </Link>
      ))}
    </div>
  );
}
